import { buttonClasses, iconClasses, Theme } from "@mui/material"
import { makeStyles } from "tss-react/mui"
// The focus-ring mixins are only on Theme via this augmentation, which the
// palette import below doesn't pull in.
import "../../core/muiThemeAugmentation"
import {
  lightBlue,
  teal,
  blueGrey,
  red,
  indigo
} from "../../core/palette"

// a11y: the blue ring measures ~1.1:1 directly on the saturated contained fills,
// so those variants ring with a white gap instead — the gap lifts the ring off
// the fill so it reads 4.9:1 against white on both sides, whatever the fill is.
// The two-tone ring does not work here: its dark line lands on the fill (2.9:1)
// and its white ring on the light page (1:1). Two-tone is for variable/branded
// backgrounds like the subject banners, not for a colored button on a light page.
// The !important beats the global MuiCssBaseline `button:focus` rule and the
// contained variant's own elevation shadow, which share this selector.
const saturatedFillFocus = (mixins: Theme["mixins"]) => ({
  ...mixins.focusRing.ringWithGap,
  // The base `&:focus-visible` paints a blue 1px border, which would otherwise
  // sit inside the gap directly against the fill at 1.6:1.
  borderColor: "transparent",
  boxShadow: `${mixins.focusRing.ringWithGap.boxShadow} !important`
})

export const useStyles = makeStyles()(
  ({ spacing, palette, typography, shadows, mixins }) => ({
    button: {
      display: "flex",
      fontWeight: 500,
      alignItems: "center",
      borderRadius: "3px",
      minWidth: "auto",
      border: "1px solid transparent",

      [`&.${buttonClasses.contained}`]: {
        boxShadow: shadows[2]
      },

      // a11y: keyboard-only ring (SC 2.4.7). :focus would also fire on mouse
      // click and leave a stuck ring; :focus-visible shows it only for keyboard.
      "&:focus": { outline: "none" },
      "&:focus-visible": mixins.focusRing.outline,

      // General sub-components styles
      [`& .${buttonClasses.startIcon}`]: {
        marginLeft: 0
      },
      [`& .${buttonClasses.endIcon}`]: {
        marginRight: 0
      },
      [`& .${iconClasses.root}`]: {
        width: "auto",
        height: "auto",
        fontSize: "inherit",
        lineHeight: "inherit"
      },

      // a11y: an aria-disabled button keeps the disabled look but stays focusable
      // and pointer-reachable (the action is guarded in JS), unlike native
      // disabled which drops it from the tab order + a11y tree.
      [`&.${buttonClasses.disabled}[aria-disabled="true"]`]: {
        pointerEvents: "auto",
        cursor: "not-allowed"
      }
    },

    // TODO: Remove this style and property as all buttons have bold text now.
    bold: {
      fontWeight: 500
    },

    // variants – colors
    contained_default: {
      color: palette.common.white,
      backgroundColor: blueGrey[500],
      "&:hover": {
        backgroundColor: blueGrey[700]
      },
      "&:focus-visible": saturatedFillFocus(mixins),
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: blueGrey[200],
        color: palette.common.white
      }
    },
    contained_primary: {
      backgroundColor: lightBlue[600],
      color: palette.common.white,
      "&:hover": {
        backgroundColor: lightBlue[800]
      },
      "&:focus-visible": saturatedFillFocus(mixins),
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: lightBlue[200],
        color: palette.common.white
      }
    },
    contained_secondary: {
      backgroundColor: teal[500],
      color: palette.common.white,
      "&:hover": {
        backgroundColor: teal[700]
      },
      "&:focus-visible": saturatedFillFocus(mixins),
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: teal[200],
        color: palette.common.white
      }
    },
    contained_white: {
      backgroundColor: palette.common.white,
      color: palette.text.primary,
      "&:hover": {
        backgroundColor: blueGrey[50]
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: blueGrey[100],
        color: blueGrey[300],

        [`& .${iconClasses.root}`]: {
          color: blueGrey[300]
        }
      }
    },
    contained_error: {
      backgroundColor: red[600],
      color: palette.common.white,
      "&:hover": {
        backgroundColor: red[700]
      },
      "&:focus-visible": saturatedFillFocus(mixins),
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: red[300],
        color: palette.common.white
      }
    },
    contained_persistent: {
      backgroundColor: indigo[800],
      color: palette.common.white,
      "&:hover": {
        backgroundColor: indigo[900]
      },
      "&:focus-visible": saturatedFillFocus(mixins),
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: indigo[300],
        color: palette.common.white
      }
    },
    outlined_default: {
      border: `1px solid ${blueGrey[200]}`,
      color: blueGrey[600],
      "&:hover": {
        backgroundColor: blueGrey[50],
        borderColor: blueGrey[200]
      },
      [`&.${buttonClasses.disabled}`]: {
        borderColor: blueGrey[50],
        color: blueGrey[50]
      }
    },
    outlined_primary: {
      border: `1px solid ${lightBlue[300]}`,
      color: lightBlue[700],
      "&:hover": {
        backgroundColor: lightBlue[50],
        borderColor: lightBlue[300]
      },
      [`&.${buttonClasses.disabled}`]: {
        borderColor: lightBlue[200],
        color: lightBlue[200]
      }
    },
    outlined_secondary: {
      border: `1px solid ${teal[300]}`,
      color: teal[600],
      "&:hover": {
        backgroundColor: teal[50],
        borderColor: teal[300]
      },
      [`&.${buttonClasses.disabled}`]: {
        borderColor: teal[200],
        color: teal[200]
      }
    },
    outlined_error: {
      border: `1px solid ${red[300]}`,
      color: red[700],
      "&:hover": {
        backgroundColor: red[50],
        borderColor: red[300]
      },
      [`&.${buttonClasses.disabled}`]: {
        borderColor: red[300],
        color: red[300]
      }
    },
    outlined_persistent: {
      border: `1px solid ${indigo[300]}`,
      color: indigo[700],
      "&:hover": {
        backgroundColor: indigo[50],
        borderColor: indigo[300]
      },
      [`&.${buttonClasses.disabled}`]: {
        borderColor: indigo[200],
        color: indigo[200]
      }
    },
    text_default: {
      color: blueGrey[600],
      "&:hover": {
        backgroundColor: blueGrey[50]
      }
    },
    text_primary: {
      color: lightBlue[700],
      "&:hover": {
        backgroundColor: lightBlue[50]
      },
      [`&.${buttonClasses.disabled}`]: {
        color: lightBlue[200]
      }
    },
    text_secondary: {
      color: teal[600],
      "&:hover": {
        backgroundColor: teal[50]
      },
      [`&.${buttonClasses.disabled}`]: {
        color: teal[200]
      }
    },
    text_error: {
      color: red[700],
      "&:hover": {
        backgroundColor: red[50]
      },
      [`&.${buttonClasses.disabled}`]: {
        color: red[200]
      }
    },
    text_persistent: {
      color: indigo[700],
      "&:hover": {
        backgroundColor: indigo[50]
      },
      [`&.${buttonClasses.disabled}`]: {
        color: indigo[200]
      }
    },
    // sizes
    xs: {
      [`.${buttonClasses.startIcon}`]: {
        marginRight: spacing(2)
      },
      [`.${buttonClasses.endIcon}`]: {
        marginLeft: spacing(2)
      },
      padding: spacing(2, 3),
      fontSize: typography.pxToRem(12),
      lineHeight: typography.pxToRem(14),
      height: typography.pxToRem(30)
    },
    sm: {
      [`.${buttonClasses.startIcon}`]: {
        marginRight: spacing(3)
      },
      [`.${buttonClasses.endIcon}`]: {
        marginLeft: spacing(3)
      },
      padding: spacing(2, 4),
      fontSize: typography.pxToRem(14),
      lineHeight: typography.pxToRem(16),
      height: typography.pxToRem(36)
    },
    md: {
      [`.${buttonClasses.startIcon}`]: {
        marginRight: spacing(4)
      },
      [`.${buttonClasses.endIcon}`]: {
        marginLeft: spacing(4)
      },
      padding: spacing(2, 4),
      fontSize: typography.pxToRem(16),
      lineHeight: typography.pxToRem(20),
      height: typography.pxToRem(42)
    },
    lg: {
      [`.${buttonClasses.startIcon}`]: {
        marginRight: spacing(4)
      },
      [`.${buttonClasses.endIcon}`]: {
        marginLeft: spacing(4)
      },
      padding: spacing(3, 5),
      fontSize: typography.pxToRem(16),
      lineHeight: typography.pxToRem(20),
      height: typography.pxToRem(50)
    },

    // a11y: `focusRing` prop overrides. Defined last so they win over the
    // variant defaults above, which carry the same specificity. Keyboard-only
    // (`:focus-visible`) to match the base ring.
    // `focusRingDefault` is the explicit spelling of the omit-the-prop default
    // (the standard blue ring), like MUI's `variant="text"`. It intentionally
    // does NOT reset boxShadow, so on a saturated contained variant the gap
    // ring's `boxShadow !important` survives — `focusRing="default"` can't
    // downgrade a saturated fill to the plain ring (which would fail 3:1).
    focusRingDefault: {
      "&:focus-visible": mixins.focusRing.outline
    },
    focusRingOnDark: {
      "&:focus-visible": {
        ...mixins.focusRingOnDark.ring,
        // Zero the base blue 1px border so it doesn't sit inside the white ring.
        borderColor: "transparent",
        boxShadow: `${mixins.focusRingOnDark.ring.boxShadow} !important`
      }
    },
    focusRingWithGap: {
      "&:focus-visible": saturatedFillFocus(mixins)
    },
    // For variable/branded backgrounds (subject banners), where neither a single
    // ring color nor a white gap holds 3:1 across the whole surface.
    focusRingTwoTone: {
      "&:focus-visible": {
        ...mixins.focusRingTwoTone.ring,
        borderColor: "transparent",
        boxShadow: `${mixins.focusRingTwoTone.ring.boxShadow} !important`
      }
    }
  })
)
